import { el } from "../lib/dom.ts";
import { getSourceType } from "../lib/typeSystem.ts";
import { getBookmarks, removeBookmark, type BookmarkItem } from "../lib/store.ts";
import { openPeek } from "./peek.ts";
import { createDrawer } from "./drawer.ts";

export interface BookmarkActions {
  onOpen?: (item: BookmarkItem) => boolean;
  onPreview?: (item: BookmarkItem) => void;
  onRemove?: (item: BookmarkItem) => void | Promise<void>;
}

const drawer = createDrawer("bookmarks-drawer", "Bookmarks");

// Focus restore inside the async-filled panel body (host sits within the sheet).
function restoreBookmarkFocus(host: HTMLElement): void {
  const next = host.querySelector<HTMLButtonElement>("button") ?? host.closest(".bookmarks-drawer")?.querySelector<HTMLButtonElement>(".drawer-close");
  next?.focus();
}

function action(label: string, aria: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", "bookmark-action", label);
  b.type = "button";
  b.setAttribute("aria-label", aria);
  b.title = aria;
  b.addEventListener("click", onClick);
  return b;
}

function previewBookmark(item: BookmarkItem, actions: BookmarkActions): void {
  if (actions.onPreview) {
    actions.onPreview(item);
    return;
  }
  const type = getSourceType(item.category);
  openPeek({
    ref: item.ref,
    label: item.enTitle,
    labelHe: item.heTitle,
    relationshipLabel: "bookmarked source",
    colorVar: type.colorVar,
  });
}

function openBookmark(item: BookmarkItem, actions: BookmarkActions): void {
  const opened = actions.onOpen?.(item) === true;
  if (opened) {
    closeBookmarksDrawer(false);
    return;
  }
  previewBookmark(item, actions);
}

function bookmarkCard(item: BookmarkItem, actions: BookmarkActions, rerender: () => void): HTMLElement {
  const type = getSourceType(item.category);
  const card = el("article", "bookmark-card");
  card.dataset.ref = item.ref;
  card.style.setProperty("--tc", `var(${type.colorVar})`);

  const head = el("div", "bookmark-head");
  const dot = el("span", "ec-dot bookmark-dot");
  dot.style.setProperty("--dot", `var(${type.colorVar})`);
  dot.setAttribute("aria-hidden", "true");
  const titles = el("div", "bookmark-titles");
  if (item.heTitle) {
    const he = el("span", "bookmark-he", item.heTitle);
    he.lang = "he";
    he.dir = "rtl";
    titles.append(he);
  }
  titles.append(el("span", "bookmark-en", item.enTitle));
  head.append(dot, titles, el("span", "bookmark-type", type.label));

  const acts = el("div", "bookmark-actions");
  acts.append(
    action("Open", `Open ${item.enTitle}`, () => openBookmark(item, actions)),
    action("Preview", `Preview ${item.enTitle}`, () => previewBookmark(item, actions)),
    action("Remove", `Remove ${item.enTitle} from Bookmarks`, () => {
      if (actions.onRemove) {
        void Promise.resolve(actions.onRemove(item)).then(rerender);
        return;
      }
      void removeBookmark(item.ref).then(rerender);
    }),
  );

  card.append(head, acts);
  return card;
}

async function fillBookmarks(host: HTMLElement, actions: BookmarkActions): Promise<void> {
  const render = async (): Promise<void> => fillBookmarks(host, actions);
  const items = await getBookmarks();
  const hadFocus = document.activeElement instanceof HTMLElement && host.contains(document.activeElement);
  host.replaceChildren();
  if (!items.length) {
    const empty = el("div", "study-empty");
    empty.append(el("div", "study-empty-title", "No bookmarks yet"));
    empty.append(el("div", "study-empty-copy", "Bookmark sources you want to return to later."));
    host.append(empty);
    if (hadFocus) restoreBookmarkFocus(host);
    return;
  }
  const list = el("div", "bookmark-list");
  for (const item of items) list.append(bookmarkCard(item, actions, () => void render()));
  host.append(list);
  if (hadFocus) restoreBookmarkFocus(host);
}

export function buildBookmarksPanel(actions: BookmarkActions = {}): HTMLElement {
  const panel = el("div", "bookmarks-panel");
  const head = el("div", "bookmarks-head");
  head.append(el("div", "bookmarks-title", "Bookmarks"));
  head.append(el("div", "bookmarks-sub", "Sources you bookmarked for later."));
  const body = el("div", "bookmarks-body");
  body.append(el("div", "journey-empty", "Loading bookmarks..."));
  panel.append(head, body);
  void fillBookmarks(body, actions);
  return panel;
}

export function openBookmarksDrawer(actions: BookmarkActions = {}): void {
  drawer.open(() => buildBookmarksPanel(actions));
}

export function refreshBookmarksDrawer(): void {
  drawer.refresh();
}

export function closeBookmarksDrawer(restoreFocus = true): void {
  drawer.close(restoreFocus);
}

export function _resetBookmarksViewForTests(): void {
  drawer.close(false);
}
